import { Avatar, IconButton, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { EyeIcon } from "@phosphor-icons/react";
import { IMAGE_URL } from "config";
import moment from "moment";
import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const ChildStructuresSection = memo(
  ({ childStructures, isLoadingChildStructures, denominationId }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Memoized columns for child structures table
    const childStructuresColumns = useMemo(
      () => [
        {
          field: "image",
          headerName: "",
          width: 60,
          sortable: false,
          filterable: false,
          renderCell: params => (
            <Avatar
              src={
                params.row.image_url
                  ? `${IMAGE_URL}/${params.row.image_url}`
                  : undefined
              }
              alt={params.row.name}
              sx={{ width: 40, height: 40 }}
            >
              {!params.row.image_url &&
                params.row.name?.charAt(0)?.toUpperCase()}
            </Avatar>
          )
        },
        {
          field: "name",
          headerName: t("denominations.structures.name"),
          flex: 1,
          minWidth: 200
        },
        {
          field: "denomination_structure_type",
          headerName: t("denominations.structures.type"),
          flex: 1,
          minWidth: 120,
          valueFormatter: params => params.value?.name || "None"
        },
        {
          field: "parent",
          headerName: t("denominations.structures.parentStructure"),
          flex: 1,
          minWidth: 180,
          valueFormatter: params => params.value?.name || "None"
        },
        {
          field: "created_at",
          headerName: t("denominations.structures.createdAt"),
          flex: 1,
          minWidth: 120,
          valueFormatter: params =>
            params.value ? moment(params.value).format("DD/MM/YYYY") : "N/A"
        },
        {
          field: "actions",
          headerName: t("denominations.structures.actions"),
          width: 80,
          sortable: false,
          filterable: false,
          renderCell: params => (
            <IconButton
              onClick={() => {
                navigate(
                  `/denomination-dashboard/${denominationId}/structures/${params.row.id}`
                );
              }}
              size="small"
            >
              <EyeIcon />
            </IconButton>
          )
        }
      ],
      [denominationId, navigate]
    );

    return (
      <Stack spacing={3}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {t("denominations.structures.childStructures")} (
          {childStructures.length})
        </Typography>
        <DataGrid
          autoHeight
          rows={childStructures}
          rowCount={childStructures.length}
          loading={isLoadingChildStructures}
          columns={childStructuresColumns}
          rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
          density="comfortable"
          getRowId={row => row.id}
          disableColumnMenu
          disableSelectionOnClick
          pagination
          paginationMode="client"
          localeText={{
            noRowsLabel: t("denominations.structures.noChildStructuresFound"),
            MuiTablePagination: {
              labelRowsPerPage: t("teams.teamProfile.RowsPerPage")
            }
          }}
        />
      </Stack>
    );
  }
);

ChildStructuresSection.displayName = "ChildStructuresSection";

export default ChildStructuresSection;
